<template>
  <ExternalQueryList
      endpoint="/api/v2/companies"
      :headlines="headlines"
      :filters="filters"
      :can-update="() => true"
      @show="company => $router.push(getUrl(company))"
  >
    <template v-slot:default="{ row: company }">
      <td>
        <div style="display: flex; justify-content: space-between">
          <div>
            <router-link :to="getUrl(company)">
              {{ company.attributes.name }}
            </router-link>
          </div>

          <div style="white-space:nowrap;">
            <i
                v-if="company.attributes.has_recent_order"
                class="fa fa-bullhorn"
                v-tooltip="'Recent advertising (within 3 months)'"
            />

            <i
                v-else-if="company.attributes.has_order"
                class="fa fa-bullhorn"
                v-tooltip="'Old advertising'"
                style="opacity: 0.2"
            />

            <i v-else class="fa fa-bullhorn" style="opacity: 0"/>

            <i
                v-if="company.attributes.has_active_partnership"
                class="fa fa-book-reader"
                v-tooltip="'Active subscription'"
            />

            <i
                v-else-if="company.attributes.has_partnership"
                class="fa fa-book-reader"
                v-tooltip="'Expired/trial subscription'"
                style="opacity: 0.2"
            />

            <i v-else class="fa fa-book-reader" style="opacity: 0"/>
          </div>
        </div>
      </td>

      <td>
        <Avatar v-for="user in company.attributes.users" :key="user.id" :color="user.color">
          {{ user.initials }}
        </Avatar>
      </td>

      <td>
        {{ company.attributes.recent_value | price }}
      </td>

      <td>
        <template v-for="tag in company.attributes.tags">
          <span
              class="label"
              :style="{'background-color': tag.color}">
              {{ tag.name }}
          </span>&nbsp;
        </template>
      </td>

      <td>
        {{ company.attributes.last_activity_at | date }}
      </td>

      <td>
        {{ company.attributes.last_sales_activity_at | date }}
      </td>
    </template>

    <template v-slot:list-actions="{selected, update, resetSelected, canUpdateSelected}">
      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#add-company-tags-modal" data-toggle="modal">
          <i class="fa fa-plus"></i> Add Tags
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#remove-company-tags-modal" data-toggle="modal">
          <i class="fa fa-minus"></i> Remove Tags
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#create-project-leads-modal" data-toggle="modal">
          <i class="fa fa-plus"></i> Create Project Leads
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected || missingPermission('admin')}">
        <a href="#update-portal-user-modal" data-toggle="modal">
          <i class="fa fa-user"></i> Update Portal User
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#delete-portal-user-modal" data-toggle="modal">
          <i class="fa fa-times"></i> Delete Portal User
        </a>
      </li>
    </template>

    <template v-slot:modals="{selected, update, resetSelected}">
      <AddCompanyTagsModal
          :selected="selected"
          @updated="updated($event, update, resetSelected)"
      />

      <RemoveCompanyTagsModal
          :selected="selected"
          @updated="updated($event, update, resetSelected)"
      />

      <CreateProjectLeadsModal
          :selected="selected"
          :projects="projects"
          @updated="resetSelected"
      />

      <UpdatePortalUserModal
          :selected="selected"
          :portals="portals"
          @updated="updated($event, update, resetSelected)"
      />

      <DeletePortalUserModal
          :selected="selected"
          :portals="portals"
          @updated="updated($event, update, resetSelected)"
      />
    </template>
  </ExternalQueryList>
</template>

<script>
import Avatar from '../../../Avatar'
import AddCompanyTagsModal from '../../../Modals/Companies/AddCompanyTagsModal'
import ExternalQueryList from '../../../Panels/Lists/ExternalQuery'
import RemoveCompanyTagsModal from '../../../Modals/Companies/RemoveCompanyTagsModal'
import CreateProjectLeadsModal from "../../../Modals/Companies/CreateProjectLeadsModal.vue";
import UpdatePortalUserModal from "../../../Modals/Companies/UpdatePortalUserModal.vue";
import DeletePortalUserModal from "../../../Modals/Companies/DeletePortalUserModal.vue";
import Permissions from "../../../../mixins/Permissions";

export default {
  components: {
    CreateProjectLeadsModal,
    Avatar,
    AddCompanyTagsModal,
    ExternalQueryList,
    RemoveCompanyTagsModal,
    UpdatePortalUserModal,
    DeletePortalUserModal
  },

  mixins: [Permissions],

  props: {
    portals: {
      required: true
    },

    projects: {
      required: true,
    }
  },

  computed: {
    headlines() {
      return [
        {width: 6, text: 'Name', sort_column: 'name'},
        {width: 4, text: 'Users'},
        {width: 2, text: 'Recent Sales', sort_column: 'recent_value'},
        {width: 4, text: 'Tags'},
        {width: 2, text: 'Last Activity', sort_column: 'last_activity_at'},
        {width: 2, text: 'Sales Activity', sort_column: 'last_sales_activity_at'}
      ]
    },

    filters() {
      return [
        [
          {
            width: 5,
            widget: 'text',
            name: 'search',
            placeholder: 'Search...'
          },
          {
            width: 5,
            widget: 'text',
            name: 'zip_code',
            placeholder: 'Search Zip Code...'
          },
          {
            width: 10,
            widget: 'new-tags',
            name: 'tag_id'
          }
        ],
        [
          {
            width: 5,
            widget: 'select',
            name: 'portal_id',
            options: this.portals.map(portal => ({value: portal.id, label: portal.attributes.name})),
            nullable: true,
            placeholder: 'Choose Portal...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'partnership_status',
            options: [
              {value: 'active', label: 'Active'},
              {value: 'expired', label: 'Expired'},
              {value: 'none', label: 'None'}
            ],
            nullable: true,
            placeholder: 'Choose Subscription Status...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'order_status',
            options: [
              {value: 'recent', label: 'Recent'},
              {value: 'old', label: 'Older than 3 months'},
              {value: 'all', label: 'Any time'},
              {value: 'none', label: 'None'}
            ],
            nullable: true,
            placeholder: 'Choose Advertising Status...'
          },
          {
            width: 4,
            widget: 'button',
            class: 'btn-white',
            text: '<i class="fa fa-times"></i> Clear',
            event: 'clear'
          }
        ],
        [
          {
            width: 5,
            widget: 'date',
            name: 'advertising.published_from',
            placeholder: 'Advertising Published From...'
          },
          {
            width: 5,
            widget: 'date',
            name: 'advertising.published_to',
            placeholder: 'Advertising Published To...'
          },
          {
            width: 5,
            widget: 'text',
            name: 'advertising.value_from',
            placeholder: 'Advertising Value From...'
          },
          {
            width: 5,
            widget: 'text',
            name: 'advertising.value_to',
            placeholder: 'Advertising Value To...'
          }
        ],
        [
          {
            width: 10,
            widget: 'typeahead',
            name: 'user_id',
            placeholder: 'Choose User...',
            endpoint: '/api/v2/users',
            resultLabelProperty: row => row.attributes.full_name
          },
          {
            width: 5,
            widget: 'text',
            name: 'industry',
            nullable: true,
            placeholder: 'Search Industries...'
          },
          {
            width: 5,
            widget: 'text',
            name: 'secondary_industries',
            nullable: true,
            placeholder: 'Search Secondary Industries...'
          },
          {
            width: 4,
            widget: 'submit'
          }
        ]
      ]
    }
  },

  methods: {
    getUrl(company) {
      return '/companies/' + company.id
    },

    updated(company, update, resetSelected) {
      update(company)

      resetSelected()
    }
  }
}
</script>
